import { Slider, SliderProps } from "@mui/material";

import { Mark } from "@mui/base";
import { VALUE_TYPE } from "Components/NumberValue";
import { formatToLocaleString } from "Formatting/formatNumber";
import { AdviceProductChangeType } from "Store/advice/interfaces";
import React from "react";
import { SliderWrapper } from "./styled";

const sliderStyle: Required<SliderProps["sx"]> = {
  color: "#d38d89",
  height: 4,
  padding: "13px 0",
  width: "90%",
  "& .MuiSlider-thumb": {
    height: 28,
    width: 28,
    backgroundColor: "#bf0900",
    boxShadow:
      "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)",
    "&:focus, &:hover, &$active": {
      boxShadow:
        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
    },
  },
  "& .MuiSlider-active": {},
  "& .MuiSlider-valueLabel": {
    background: "transparent",
    color: "#bf0900",
    fontSize: "18px",
    fontWeight: "600",
    top: 0,
    width: "max-content",
  },
  "& .MuiSlider-rail": {
    color: "#000000",
    opacity: 1,
    height: 4,
    borderRadius: 4,
  },
  "& .MuiSlider-mark": {
    display: "none",
  },
  "& .MuiSlider-markLabel": {
    color: "#000000",
  },
};

interface Props {
  setCurrentSliderValue: (value: number) => void;
  handleCustomerChange: (
    changeType: AdviceProductChangeType,
    changeValue: number | boolean,
  ) => void;
  step: number | null;
  min: number;
  minAdvice: number | null;
  maxAdvice: number | null;
  max: number;
  adviceValue: number;
  defaultValue: number;
  adviceValueMarkLabel: string;
  valueType: VALUE_TYPE;
  disabled?: boolean;
  label: string;
}

export const DiscreteSlider = ({
  setCurrentSliderValue,
  handleCustomerChange,
  step,
  min,
  minAdvice,
  maxAdvice,
  max,
  adviceValue,
  defaultValue,
  adviceValueMarkLabel,
  valueType,
  disabled,
  label,
}: Props): React.ReactElement => {
  const trackLeft = (): number => {
    const value = (((minAdvice ?? min) - min) * 100) / (max - min);
    return Math.round(value);
  };

  const trackWidth = (): number => {
    const value = (((maxAdvice ?? min) - min) * 100) / (max - min);
    return Math.round(value) - trackLeft();
  };

  const valuetext = (value: number): string => {
    return `${formatToLocaleString(value)} ${valueType}`;
  };

  const sx: Required<SliderProps["sx"]> = {
    ...sliderStyle,
    "& .MuiSlider-track": {
      height: 4,
      left: `${trackLeft()}% !important`,
      width: `${trackWidth()}% !important`,
    },
  };

  const marks: Mark[] = [
    {
      value: min,
      label: `${formatToLocaleString(min)} ${valueType}`,
    },
  ];
  if (minAdvice !== null && maxAdvice !== null) {
    marks.push(
      ...[
        { value: minAdvice },
        { value: adviceValue, label: adviceValueMarkLabel },
        { value: maxAdvice },
      ],
    );
  }
  marks.push({
    value: max,
    label: `${formatToLocaleString(max)} ${valueType}`,
  });

  return (
    <SliderWrapper className="AdviceSlider">
      <Slider
        sx={sx}
        value={defaultValue}
        getAriaValueText={valuetext}
        onChange={(_, value) => setCurrentSliderValue(value as number)}
        onChangeCommitted={(_, value) =>
          handleCustomerChange(
            AdviceProductChangeType.PRODUCT_SLIDER,
            value as number,
          )
        }
        disabled={disabled}
        valueLabelFormat={valuetext}
        aria-label={label}
        valueLabelDisplay="on"
        step={step}
        marks={marks}
        min={min}
        max={max}
      />
    </SliderWrapper>
  );
};
